import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [twoFaState, setTwoFaState] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          if (response.data.requires_2fa) {
            setTwoFaState({ temp_token: response.data.temp_token, email: response.data.email });
            toast.success("Verification code sent to your email");
          } else if (response.data.token) {
            login(response.data);
            toast.success("Welcome back!");
            window.history.replaceState(null, "", "/dashboard");
            navigate("/dashboard", { replace: true });
          } else {
            login(response.data);
            window.history.replaceState(null, "", "/dashboard");
            navigate("/dashboard", { replace: true, state: { user: response.data } });
          }
        } catch (error) {
          toast.error("Authentication failed");
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, login]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (twoFaCode.length !== 6) return;
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify`, {
        temp_token: twoFaState.temp_token,
        code: twoFaCode,
        remember_device: rememberDevice,
      }, { withCredentials: true });
      if (response.data.token) {
        login(response.data);
        toast.success("Welcome back!");
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/auth/2fa/resend`, { temp_token: twoFaState.temp_token });
      toast.success("New code sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  if (twoFaState) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#080d19] p-4">
        <div className="w-full max-w-sm rounded-2xl border border-white/[0.12] bg-[#1a2440]/80 backdrop-blur-xl p-7 shadow-2xl">
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="text-center mb-2">
              <div className="w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Verify your identity</h3>
              <p className="text-xs text-slate-400 mt-1">Enter the 6-digit code sent to<br/><span className="text-cyan-400">{twoFaState.email}</span></p>
            </div>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              className="glass-input h-14 text-center text-2xl tracking-[0.5em] font-mono"
              value={twoFaCode}
              onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoFocus
              data-testid="2fa-code-input-google"
            />
            <Button type="submit" className="w-full h-11 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-lg" disabled={isLoading || twoFaCode.length !== 6} data-testid="2fa-verify-btn-google">
              {isLoading ? "Verifying..." : "Verify & Sign In"}
            </Button>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-device-label-google">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                data-testid="remember-device-checkbox-google"
              />
              <span className="text-xs text-slate-400">Remember this device for 30 days</span>
            </label>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={handleResend} disabled={resending} className="text-slate-500 hover:text-cyan-400 transition-colors" data-testid="2fa-resend-btn-google">
                {resending ? "Sending..." : "Resend code"}
              </button>
              <button type="button" onClick={() => navigate("/")} className="text-slate-500 hover:text-cyan-400 transition-colors">
                Back to login
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-sky-400 animate-spin mx-auto mb-4" />
        <p className="text-zinc-400">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
